import { RegisterOptions, Validate } from 'react-hook-form';

type ValidateFormRule<T extends string> = Record<T, RegisterOptions>;

export class VALIDATE_RULES {
  /**
   * 必須項目プロパティ設定時のエラーメッセージ
   */
  private static readonly REQUIRED_ERROR_MESSAGE = '必須項目です';

  /**
   * メールアドレスのバリデーション設定
   */
  private static readonly EMAIL_VALIDATE_RULE = {
    pattern: {
      value:
        /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/,
      message: 'メールアドレスの形式が正しくありません'
    },
    // https://html.spec.whatwg.org/multipage/input.html#valid-e-mail-address
    required: VALIDATE_RULES.REQUIRED_ERROR_MESSAGE
  };

  /**
   * パスワードのバリデーション設定
   */
  private static readonly PASSWORD_VALIDATE_RULE = {
    pattern: {
      value: /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?\d)[!-~]{8,32}$/,
      message: '8文字以上・32文字以下・半角英大小文字、数字を各1回以上利用してください'
    },
    // [8文字以上, 32文字以内, 半角英大文字、半角英小文字、数字を各1回以上使用]
    required: VALIDATE_RULES.REQUIRED_ERROR_MESSAGE
  };

  /**
   * 確認用パスワードのバリデータを返す
   */
  public static readonly PASSWORD_CONFIRMATION_VALIDATE_RULE = (password: string) => {
    return {
      required: VALIDATE_RULES.REQUIRED_ERROR_MESSAGE,
      validate: (value: string) => value === password || 'パスワードが一致していません'
    };
  };

  /**
   * プロフィール/名前のバリデーション設定
   */
  private static readonly PROFILE_ATTR_NAME_VALIDATE_RULE = {
    required: VALIDATE_RULES.REQUIRED_ERROR_MESSAGE,
    maxLength: {
      value: 50,
      message: '50文字以下に文字数納めてください'
    }
  };

  /**
   * カバー画像のバリデーション設定
   */
  private static readonly COVER_IMAGE_VALIDATE_RULE = {
    validate: {
      type: (file?: File) => !file || /^image\/.*/.test(file.type) || '画像ファイルを選択してください',
      size: (file?: File) => !file || file.size <= 1024000 || '1MB以下の画像ファイルをアップロードできます'
    }
  };

  /**
   * アイコン画像のバリデーション設定
   */
  private static readonly ICON_IMAGE_VALIDATE_RULE = {
    validate: {
      type: (file?: File) => !file || /^image\/.*/.test(file.type) || '画像ファイルを選択してください',
      size: (file?: File) => !file || file.size <= 1024000 || '1MB以下の画像ファイルをアップロードできます'
    }
  };

  /**
   * アップロードできる添付ファイル上限
   * 50MB
   */
  public static readonly LESSON_FILE_UPLOAD_LIMIT = 52428800;

  /**
   * SignInFormで利用するバリデーションルール
   */
  public static readonly SIGN_IN_FORM: ValidateFormRule<'email' | 'password'> = {
    email: VALIDATE_RULES.EMAIL_VALIDATE_RULE,
    // TODO: 初回開発ユーザーのパスワードがパスワード設定要件と異なる為、一時的に除外
    // password: VALIDATE_RULES.PASSWORD_VALIDATE_RULE
    password: { required: VALIDATE_RULES.PASSWORD_VALIDATE_RULE.required }
  };

  /**
   * for Register/Edit
   */
  public static readonly REGISTER_EDIT_FORM: ValidateFormRule<'password' | 'password_confirmation' | 'name'> = {
    password: VALIDATE_RULES.PASSWORD_VALIDATE_RULE,
    password_confirmation: VALIDATE_RULES.PASSWORD_VALIDATE_RULE,
    name: VALIDATE_RULES.PROFILE_ATTR_NAME_VALIDATE_RULE
  };

  /**
   * SignUpFormで利用するバリデーションルール
   */
  public static readonly SIGN_UP_FORM: ValidateFormRule<'email'> = {
    email: VALIDATE_RULES.EMAIL_VALIDATE_RULE
  };

  /**
   * SchoolFormで利用するバリデーションルール
   */
  public static readonly SCHOOL_FORM: ValidateFormRule<
    'name' | 'catchphrase' | 'description' | 'coverImage' | 'iconImage' | 'logoImage'
  > = {
    name: {
      required: VALIDATE_RULES.REQUIRED_ERROR_MESSAGE,
      maxLength: {
        value: 50,
        message: '50文字以下に文字数を納めてください'
      }
    },
    catchphrase: {
      required: VALIDATE_RULES.REQUIRED_ERROR_MESSAGE,
      maxLength: {
        value: 50,
        message: '50文字以下に文字数を納めてください'
      }
    },
    description: {
      required: VALIDATE_RULES.REQUIRED_ERROR_MESSAGE
    },
    coverImage: VALIDATE_RULES.COVER_IMAGE_VALIDATE_RULE,
    iconImage: VALIDATE_RULES.ICON_IMAGE_VALIDATE_RULE,
    logoImage: VALIDATE_RULES.ICON_IMAGE_VALIDATE_RULE
  };

  /**
   * TeacherFormで利用するバリデーションルール
   */
  public static readonly TEACHER_FORM: ValidateFormRule<'name' | 'title' | 'profile' | 'iconImage'> = {
    name: {
      required: VALIDATE_RULES.REQUIRED_ERROR_MESSAGE,
      maxLength: {
        value: 50,
        message: '50文字以下に文字数を納めてください'
      }
    },
    title: {
      required: VALIDATE_RULES.REQUIRED_ERROR_MESSAGE,
      maxLength: {
        value: 20,
        message: '20文字以下に文字数を納めてください'
      }
    },
    profile: {
      required: VALIDATE_RULES.REQUIRED_ERROR_MESSAGE
    },
    iconImage: VALIDATE_RULES.ICON_IMAGE_VALIDATE_RULE
  };

  /**
   * CourseFormで利用するバリデーションルール
   */
  public static readonly COURSE_FORM: ValidateFormRule<'name' | 'description' | 'price' | 'coverImage' | 'isPaidPlan'> =
    {
      name: {
        required: VALIDATE_RULES.REQUIRED_ERROR_MESSAGE,
        maxLength: {
          value: 50,
          message: '50文字以下に文字数を納めてください'
        }
      },
      description: {
        required: VALIDATE_RULES.REQUIRED_ERROR_MESSAGE,
        maxLength: {
          value: 50,
          message: '50文字以下に文字数を納めてください'
        }
      },
      price: {
        required: VALIDATE_RULES.REQUIRED_ERROR_MESSAGE,
        valueAsNumber: true,
        min: {
          value: 0,
          message: '0円以上の金額に設定してください'
        }
      },
      coverImage: VALIDATE_RULES.COVER_IMAGE_VALIDATE_RULE,
      isPaidPlan: {}
    };

  /**
   * LessonFormで利用するバリデーションルール
   */
  public static readonly LESSON_FORM: ValidateFormRule<'name' | 'body' | 'video' | 'image' | 'attachment'> = {
    name: {
      required: VALIDATE_RULES.REQUIRED_ERROR_MESSAGE,
      maxLength: {
        value: 50,
        message: '50文字以下に文字数を納めてください'
      }
    },
    body: {
      required: VALIDATE_RULES.REQUIRED_ERROR_MESSAGE
    },
    video: {
      validate: {
        type: (file?: File) => !file || /^video\/mp4$/.test(file.type) || '拡張子mp4のファイルを選択してください',
        size: (file?: File) => !file || file.size <= 3221225472 || '3GB以下の拡張子mp4のファイルをアップロードできます'
      }
    },
    image: {
      validate: {
        type: (file?: File) => !file || /^image\/.*/.test(file.type) || '画像ファイルを選択してください',
        size: (file?: File) => !file || file.size <= 52428800 || '50MB以下の画像ファイルをアップロードできます'
      }
    },
    attachment: {
      validate: {
        size: (file?: File) => !file || file.size <= 52428800 || '50MB以下のファイルをアップロードできます'
      }
    }
  };

  /**
   * PasswordChangeFormで利用するバリデーションルール
   */
  public static readonly PASSWORD_CHANGE_FORM: ValidateFormRule<'oldPassword' | 'newPassword' | 'passwordConfirm'> = {
    oldPassword: { required: VALIDATE_RULES.REQUIRED_ERROR_MESSAGE },
    newPassword: VALIDATE_RULES.PASSWORD_VALIDATE_RULE,
    passwordConfirm: VALIDATE_RULES.PASSWORD_VALIDATE_RULE
  };

  /**
   * PasswordResetFormで利用するバリデーションルール
   */
  public static readonly PASSWORD_RESET_FORM: ValidateFormRule<'password' | 'password_confirmation'> = {
    password: VALIDATE_RULES.PASSWORD_VALIDATE_RULE,
    password_confirmation: VALIDATE_RULES.PASSWORD_VALIDATE_RULE
  };
  /**
   * パスワード変更フォームで使用するバリデーション
   */
  public static readonly PASSWORD_EDIT_FORM: ValidateFormRule<
    'current_password' | 'password' | 'password_confirmation'
  > = {
    current_password: VALIDATE_RULES.PASSWORD_VALIDATE_RULE,
    password: VALIDATE_RULES.PASSWORD_VALIDATE_RULE,
    password_confirmation: VALIDATE_RULES.PASSWORD_VALIDATE_RULE
  };
  /**
   * AccountInfoで利用するバリデーションルール
   */
  public static readonly ACCOUNT_INFO_FORM: ValidateFormRule<'name'> = {
    name: VALIDATE_RULES.PROFILE_ATTR_NAME_VALIDATE_RULE
  };
  /**
   * メールアドレス変更で利用するバリデーションルール
   */
  public static readonly EMAIL_EDIT_FORM: ValidateFormRule<'new_email'> = {
    new_email: VALIDATE_RULES.EMAIL_VALIDATE_RULE
  };
  /**
   * AccountInfoで利用するバリデーションルール
   */
  public static readonly INVITE_ADMIN_MEMBER_FORM: ValidateFormRule<'email'> = {
    email: VALIDATE_RULES.EMAIL_VALIDATE_RULE
  };
}
